import { Box, Button, Modal, RadioGroup, toast, Toggle } from '@palmetto/palmetto-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { ChangeEvent, useMemo } from 'react';
import * as yup from 'yup';

import { HoldbackPayoutEventsSettings, HoldbackPayoutEventType, PayoutEvent } from 'types';

const MILESTONE_FIELDS = {
  installApproved: 'installApproved',
  ptoApproved: 'ptoApproved',
  activationApproved: 'activationApproved',
} as const;

type MilestoneField = (typeof MILESTONE_FIELDS)[keyof typeof MILESTONE_FIELDS];

interface HoldbackPayoutEventsFormValues {
  installApproved: 'true' | 'false';
  activationApproved: 'true' | 'false';
  ptoApproved: 'true' | 'false';
  inherit: boolean;
}

interface HoldbackSettingsEditModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (values: HoldbackPayoutEventsFormValues) => Promise<void>;
  currentHoldbackSettings?: HoldbackPayoutEventsSettings;
  modalId: string;
  modalTitle: string;
  submitButtonText?: string;
  showPtoApproved?: boolean;
}

const yesNoOptions = [
  { value: 'true', label: 'Yes', id: 'yes' },
  { value: 'false', label: 'No', id: 'no' },
];

export const HoldbackPayoutEventsSettingsEditModal = ({
  isOpen,
  handleClose,
  handleSubmit,
  currentHoldbackSettings,
  modalId,
  modalTitle,
  submitButtonText = 'Save',
  showPtoApproved = false,
}: HoldbackSettingsEditModalProps) => {
  const initialValues = useMemo((): HoldbackPayoutEventsFormValues => {
    const eventActiveMap = new Map(
      currentHoldbackSettings?.holdbackPayoutEvents?.map((e) => [e.event, e.active]) ?? [],
    );

    const getEventValue = (event: HoldbackPayoutEventType): 'true' | 'false' =>
      eventActiveMap.has(event) ? (String(Boolean(eventActiveMap.get(event))) as 'true' | 'false') : 'false';

    return {
      installApproved: getEventValue(PayoutEvent.installApproved),
      activationApproved: getEventValue(PayoutEvent.activationApproved),
      ptoApproved: getEventValue(PayoutEvent.ptoApproved),
      inherit: currentHoldbackSettings?.inherit ?? false,
    };
  }, [currentHoldbackSettings]);
  const validationSchema = yup.object().shape({
    installApproved: yup.string().oneOf(['true', 'false']).required(),
    activationApproved: yup.string().oneOf(['true', 'false']).required(),
    ptoApproved: yup.string().oneOf(['true', 'false']).required(),
    inherit: yup.boolean(),
  });

  const showErrorToast = (message: string) => {
    toast.error(message, {
      style: { maxWidth: '500px', whiteSpace: 'pre-line', wordBreak: 'break-word' },
    });
  };

  const onSubmit = async (
    values: HoldbackPayoutEventsFormValues,
    { setSubmitting, resetForm }: FormikHelpers<HoldbackPayoutEventsFormValues>,
  ) => {
    try {
      await handleSubmit({ ...values });
      resetForm();
      handleClose();
      toast.success('Holdback settings updated successfully');
    } catch (error: any) {
      showErrorToast(`Failed to update holdback settings: ${error?.data?.message || String(error)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {({ isSubmitting, resetForm, submitForm, values, setFieldValue, setValues }) => {
        const handleMilestoneChange = (field: MilestoneField, value: string) => {
          if (value === 'true') {
            setValues({
              ...values,
              ...Object.values(MILESTONE_FIELDS).reduce(
                (acc, f) => ({ ...acc, [f]: f === field ? 'true' : 'false' }),
                {},
              ),
            });
          } else {
            setFieldValue(field, value);
          }
        };

        const noMilestoneActive =
          !values.inherit &&
          values.installApproved === 'false' &&
          values.ptoApproved === 'false' &&
          values.activationApproved === 'false';

        return (
          <Form>
            <Modal
              isOpen={isOpen}
              maxWidth="4xl"
              ariaLabelledBy={modalId}
              onDismiss={() => {
                if (!isSubmitting) {
                  resetForm();
                  handleClose();
                }
              }}
            >
              <Modal.Header
                id={modalId}
                title={modalTitle}
                onDismiss={() => {
                  if (!isSubmitting) {
                    resetForm();
                    handleClose();
                  }
                }}
              />
              <Modal.Body background="secondary" childGap="lg">
                <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                  <Box padding="md" radius="md">
                    <Box direction="row" justifyContent="space-between" alignItems="center" gap="md" margin="0 0 md">
                      <Box fontWeight="medium">Milestone Name</Box>
                      <Box fontWeight="medium" textAlign={{ base: 'left', desktop: 'right' }}>
                        Setting
                      </Box>
                    </Box>

                    <Box direction="row" justifyContent="space-between" alignItems="center" gap="md" margin="md 0">
                      <Box>Install Approved</Box>
                      <Box width="lg" textAlign={{ base: 'left', desktop: 'right' }} minWidth="30">
                        <RadioGroup
                          name={MILESTONE_FIELDS.installApproved}
                          value={values.installApproved}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleMilestoneChange(MILESTONE_FIELDS.installApproved, e.target.value)
                          }
                          options={yesNoOptions}
                          direction="row"
                          isDisabled={isSubmitting || values.inherit}
                        />
                      </Box>
                    </Box>

                    {showPtoApproved && (
                      <Box direction="row" justifyContent="space-between" alignItems="center" gap="md" margin="md 0">
                        <Box>PTO Approved</Box>
                        <Box width="lg" textAlign={{ base: 'left', desktop: 'right' }} minWidth="30">
                          <RadioGroup
                            name={MILESTONE_FIELDS.ptoApproved}
                            value={values.ptoApproved}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              handleMilestoneChange(MILESTONE_FIELDS.ptoApproved, e.target.value)
                            }
                            options={yesNoOptions}
                            direction="row"
                            isDisabled={isSubmitting || values.inherit}
                          />
                        </Box>
                      </Box>
                    )}

                    <Box direction="row" justifyContent="space-between" alignItems="center" gap="md" margin="md 0">
                      <Box>Activation Approved</Box>
                      <Box width="lg" textAlign={{ base: 'left', desktop: 'right' }} minWidth="30">
                        <RadioGroup
                          name={MILESTONE_FIELDS.activationApproved}
                          value={values.activationApproved}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleMilestoneChange(MILESTONE_FIELDS.activationApproved, e.target.value)
                          }
                          options={yesNoOptions}
                          direction="row"
                          isDisabled={isSubmitting || values.inherit}
                        />
                      </Box>
                    </Box>

                    {noMilestoneActive && (
                      <Box color="danger" fontSize="sm">
                        At least one milestone must be set to Yes.
                      </Box>
                    )}
                  </Box>
                </Box>

                <Toggle
                  id="inherit"
                  label="Inheriting from parent"
                  isChecked={values.inherit}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('inherit', e.target.checked);
                  }}
                  isDisabled={isSubmitting}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  tone="neutral"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  onClick={() => {
                    resetForm();
                    handleClose();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting || noMilestoneActive}
                  type="submit"
                  onClick={submitForm}
                >
                  {submitButtonText}
                </Button>
              </Modal.Footer>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};
